import { AppBar, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Toolbar, Tooltip, Typography, Chip, Box } from '@mui/material';
import React, { useEffect } from 'react';
import LoadingBackdrop from '../../Common/LoadingBackdrop';
import { Add, Delete, Edit } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useApiKeys } from '../../../hooks/useApiKeys';
import Swal from 'sweetalert2';
import MainContentContainer from '../../MainContentContainer/MainContentContainer';

export function formatDate(dateStr) {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function truncate(str, maxLen = 50) {
    if (!str) return '';
    return str.length > maxLen ? str.substring(0, maxLen) + '...' : str;
}

export default function ApiKeysPage() {
    const navigate = useNavigate();
    const { apiKeys, loading, fetchAll, revoke } = useApiKeys();

    useEffect(() => {
        fetchAll();
    }, []);

    const handleRevoke = (id, name) => {
        Swal.fire({
            title: 'Revoke API Key?',
            text: `"${name}" will be permanently revoked. Any integrations using this key will stop working.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, revoke it!',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.value) {
                revoke(id);
            }
        });
    };

    return (
        <MainContentContainer>
            <LoadingBackdrop open={loading} />
            <AppBar position="sticky">
                <Toolbar>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        API Keys
                    </Typography>
                    <Button color="inherit" component={Link} to="/api-keys/create" startIcon={<Add />}>
                        Create New Key
                    </Button>
                </Toolbar>
            </AppBar>

            <TableContainer component={Paper} sx={{ marginTop: 3 }}>
                <Table aria-label="api keys table">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell><strong>Name</strong></TableCell>
                            <TableCell><strong>Description</strong></TableCell>
                            <TableCell><strong>Owner</strong></TableCell>
                            <TableCell><strong>Scopes</strong></TableCell>
                            <TableCell><strong>Expires</strong></TableCell>
                            <TableCell><strong>Last Used</strong></TableCell>
                            <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {apiKeys.length > 0 ? (
                            apiKeys.map(token => (
                                <TableRow key={token.id}>
                                    <TableCell>
                                        <Typography variant="subtitle2">{token.name}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {truncate(token.description)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {token.tokenable?.admin_name || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {token.abilities && token.abilities.length > 0 && !(token.abilities.length === 1 && token.abilities[0] === '*') ? (
                                                token.abilities.map((scope, i) => (
                                                    <Chip key={i} label={scope} size="small" variant="outlined" />
                                                ))
                                            ) : (
                                                <Chip label="All" size="small" color="info" />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {token.is_expired ? (
                                            <Chip label="Expired" size="small" color="error" />
                                        ) : (
                                            <Typography variant="body2">
                                                {token.expires_at ? formatDate(token.expires_at) : 'Never'}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDate(token.last_used_at)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Edit" placement="top">
                                            <IconButton color="info" onClick={() => navigate(`/api-keys/${token.id}/edit`)}>
                                                <Edit />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Revoke" placement="top">
                                            <IconButton color="error" onClick={() => handleRevoke(token.id, token.name)}>
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : !loading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                        No API keys found. Create one to get started.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </TableContainer>
        </MainContentContainer>
    );
}
